import { NonFieldError } from "@/components/common/SCAlert";
import { ScrollToTop } from "@/components/common/ScrollToTop";
import { useScreenLock } from "@/components/ScreenLock";
import {
  patchProjectResendConfirmationCode,
  putProjectRegister,
} from "@/components/examinee/participation/ParticipationAPI";
import { GeneralError } from "@/components/sleep_checkup_v1/GeneralError";
import { SCSnackbar } from "@/components/sleep_checkup_v1/SCSnackbar";
import { TextForm } from "@/components/sleep_checkup_v1/TextForm";
import {
  callUserTypeApi,
  CognitoAuthenticationResultStore,
  CognitoRefreshTokenStore,
  SleepCheckupUserStore,
} from "@/utils/auth";
import { getApiServerUrl, getAxios } from "@/utils/axios";
import {
  Button,
  Dialog,
  DialogContent,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { AxiosResponse } from "axios";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// このページが受け取るstateの型
export type StateType = {
  urlId: string;
  email: string;
  password: string;
  /** 姓（前画面で入力・fail-fast 検証済み。本登録の照合にも使う） */
  lastName: string;
  /** 名（前画面で入力・fail-fast 検証済み。本登録の照合にも使う） */
  firstName: string;
  /** 生年月日（前画面で入力済み。"YYYY-MM-DD" 形式。本登録の照合にも使う） */
  birthdayWithHyphen: string;
  entryId: string;
};

/**
 * プロジェクト固有 URL からのアカウント登録の確認コード入力ページ。
 * 確認コード検証＋本登録（PUT /api/participation/project_entry/{id}/）に成功すると
 * ログイン状態になり、マイページへ遷移する。
 */
export const ProjectConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const API_URL = getApiServerUrl();

  const [errorMessage, setErrorMessage] = useState("");
  const [snackbarText, setSnackbarText] = useState("");
  const [cognitoCode, setCognitoCode] = useState("");
  const [toShowResendDialog, setToShowResendDialog] = useState(false);
  const [isLocked, setLock] = useScreenLock();

  const {
    urlId,
    email,
    password,
    lastName,
    firstName,
    birthdayWithHyphen,
    entryId,
  } = (location.state as StateType) || {};

  if (
    !urlId ||
    !email ||
    !password ||
    !lastName ||
    !firstName ||
    !birthdayWithHyphen ||
    !entryId
  ) {
    return <GeneralError to="" />;
  }

  const validators: Record<string, () => string[] | null> = {
    cognitoCode: () => {
      if (!cognitoCode.match(/^\d{6}$/)) {
        return ["※半角数字6桁で入力してください"];
      }
      return null;
    },
  };

  const handleClickConfirm = () => {
    if (Object.values(validators).some((v) => v())) {
      // 何かしらのエラーがある場合は処理を行わない
      return;
    }

    if (isLocked) {
      return;
    }
    setLock(true);
    putProjectRegister(entryId, {
      email,
      password,
      cognito_verification_code: cognitoCode,
      url_id: urlId,
      last_name: lastName,
      first_name: firstName,
      birthday: birthdayWithHyphen,
    })
      .then((res) => {
        CognitoAuthenticationResultStore.setItem(res.data.AuthenticationResult);
        CognitoRefreshTokenStore.setItem({
          RefreshToken: res.data.AuthenticationResult.RefreshToken,
        });
        callUserTypeApi(
          getAxios(API_URL),
          res.data.AuthenticationResult.IdToken
        )
          .then((userTypeRes: AxiosResponse) => {
            SleepCheckupUserStore.setItem(userTypeRes.data);
            navigate("/examinee/mypage");
          })
          .finally(() => setLock(false));
      })
      .catch((err) => {
        const statusCode = err.response?.status;
        const messages = err.response?.data?.error_messages;
        if (
          err.response?.data?.error_code === "examinee_identity_mismatch" ||
          err.response?.data?.last_name ||
          err.response?.data?.first_name ||
          err.response?.data?.birthday
        ) {
          // 前画面で fail-fast 検証を通った姓・名・生年月日が本登録時に照合できない
          // のは、検証後に他の受診者が同じ氏名・生年月日で紐付けを完了した場合など。
          // このメールアドレスでの登録は既に開始されており、同じ URL からやり直しても
          // 確認コードが再送されないため、問い合わせを案内する。
          setErrorMessage(
            "氏名または生年月日が確認できませんでした。既に登録済みの可能性があります。" +
              "実施企業・団体のご担当者にお問い合わせください。"
          );
        } else if (statusCode === 404) {
          // メールアドレスが登録済みの場合、登録 API は成功を装いつつ実体を作らないため
          // ここで初めて 404 になる（重複を悟らせない仕様）。
          setErrorMessage(
            "登録を完了できませんでした。入力されたメールアドレスが既に登録済みの可能性があります。" +
              "既にアカウントをお持ちの場合は、配布されたURLから「既にアカウントをお持ちの方」を選んでログインしてください。"
          );
        } else if (
          statusCode &&
          400 <= statusCode &&
          statusCode < 500 &&
          messages &&
          messages.length > 0
        ) {
          // 400系エラーでエラーメッセージが存在する場合はそれを表示する
          setErrorMessage(messages[0]);
        } else {
          setErrorMessage(
            "エラーが発生しました。申し訳ありませんが、少し時間をおいてから再度お試しください。"
          );
        }

        // エラーアラートが画面内に表示されるようにスクロールをリセットする
        window.scrollTo(0, 0);

        setLock(false);
      });
  };

  const handleClickResend = () => {
    setToShowResendDialog(false);
    patchProjectResendConfirmationCode(entryId, urlId)
      .then(() => {
        setSnackbarText("確認コードを再送しました。");
      })
      .catch((err) => {
        const statusCode = err.response?.status;
        const messages = err.response?.data?.error_messages;
        if (
          statusCode &&
          400 <= statusCode &&
          statusCode < 500 &&
          messages &&
          messages.length > 0
        ) {
          setErrorMessage(messages[0]);
        } else {
          setErrorMessage(
            "エラーが発生しました。申し訳ありませんが、少し時間をおいてから再度お試しください。"
          );
        }
      });
  };

  return (
    <Stack sx={{ px: 4, gap: 6 }}>
      <ScrollToTop />
      <LinearProgress
        variant="determinate"
        value={(3 / 6) * 100}
        sx={{
          borderRadius: 1,
          my: 6,
          height: "6px",
        }}
      />
      <Typography
        variant="h6"
        align="center"
        sx={{
          fontFamily: "Noto Sans JP",
          fontWeight: 500,
          fontSize: "20px",
          lineHeight: "170%",
          letterSpacing: "0.15px",
          align: "center",
        }}
      >
        確認コードの入力
      </Typography>

      {/* エラーメッセージ */}
      <NonFieldError>{errorMessage}</NonFieldError>
      {email && (
        <>
          <Typography
            variant="subtitle2"
            textAlign="justify"
            sx={{ color: "text.secondary", wordBreak: "break-all" }}
          >
            入力したメールアドレス({email}
            )に利用者登録用の確認コードをお送りしました。記載されている確認コードを入力してください。
          </Typography>

          <TextForm
            title="確認コード"
            placeholder="数字6桁"
            value={cognitoCode}
            onChange={(e) => {
              setCognitoCode(e.target.value);
            }}
            validator={validators.cognitoCode}
          />
        </>
      )}
      <Stack sx={{ mt: 6, mb: 16, gap: 4 }} alignItems="center">
        <Stack width="100%">
          <Button
            variant="contained"
            size="large"
            onClick={handleClickConfirm}
            disabled={Object.values(validators).some((v) => v())}
          >
            確認する
          </Button>
        </Stack>
        <Button
          variant="text"
          sx={{ p: 0 }}
          onClick={() => {
            setToShowResendDialog(true);
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", textDecoration: "underline" }}
          >
            コードを再送する
          </Typography>
        </Button>
      </Stack>

      {/* コードを再送するダイアログ */}
      <Dialog
        maxWidth="md"
        open={toShowResendDialog}
        onClose={() => {
          setToShowResendDialog(false);
        }}
        PaperProps={{
          style: { borderRadius: 16 },
        }}
      >
        <DialogContent sx={{ m: 0, p: 6 }}>
          <Stack spacing={2} sx={{ mb: 6 }}>
            <Typography variant="body1" sx={{ color: "text.primary" }}>
              以下のメールアドレス宛に確認コードを再送します。
            </Typography>
            <Typography variant="body2" sx={{ color: "primary.main" }}>
              {email}
            </Typography>
          </Stack>
          <Stack spacing={4} direction="row" justifyContent="flex-end">
            <Button
              variant="outlined"
              size="large"
              onClick={() => {
                setToShowResendDialog(false);
              }}
              sx={{ width: "100%" }}
            >
              キャンセル
            </Button>
            <Button
              variant="contained"
              size="large"
              sx={{ width: "100%" }}
              onClick={() => {
                setToShowResendDialog(false);
                handleClickResend();
              }}
            >
              再送する
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <SCSnackbar
        open={!!snackbarText}
        severity="success"
        onClose={() => setSnackbarText("")}
      >
        {snackbarText}
      </SCSnackbar>
    </Stack>
  );
};
